import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import I18n from 'i18n-js';
import _get from 'lodash/get';
import { Row, Col, Steps, Icon } from 'grab-ui';
import { ongoingDeliveriesV2Selector } from 'selectors/new-history-selectors';
import { ExceptMobile } from 'components/v2/responsive/responsive';
import DivButton from 'components/v2/div-button/div-button';
import { GE_ORDER_STATE, GE_WEB_ORDER_STATE } from 'constants/order-constants';
import ScribeDuck from 'ducks/scribe-duck';
import TrackGADuck from 'ducks/track-ga-duck';
import styles from './ongoing-order-section.less';
import commonStyles from './common.less';

const OngoingOrderSection = ({ goToBookingsTab }) => {
  const dispatch = useDispatch();
  const { deliveries } = useSelector(ongoingDeliveriesV2Selector);

  const summary = useMemo(
    () => {
      let processing = 0;
      let allocating = 0;
      let pickingUp = 0;
      let droppingOff = 0;

      deliveries.forEach(order => {
        switch (order.status) {
          case GE_ORDER_STATE.PendingReturn:
          case GE_ORDER_STATE.Returning:
            break;
          case GE_ORDER_STATE.Delivering: {
            const orderEvents = order.orderEvents;
            const lastEvent = _get(
              orderEvents,
              [_get(orderEvents, 'length', 0) - 1, 'State'],
              '',
            );
            if (lastEvent.toUpperCase() === 'PICKING_UP') {
              pickingUp += 1;
            } else {
              droppingOff += 1;
            }
            break;
          }
          case GE_WEB_ORDER_STATE.Allocating:
            allocating += 1;
            break;
          case GE_ORDER_STATE.PickingUp:
          case GE_ORDER_STATE.PendingPickUp:
            pickingUp += 1;
            break;
          case GE_ORDER_STATE.DroppingOff:
          case GE_ORDER_STATE.PendingDropOff:
            droppingOff += 1;
            break;
          default:
            processing += 1;
            break;
        }
      });

      return {
        total: deliveries.length,
        processing: processing || '--',
        allocating: allocating || '--',
        pickingUp: pickingUp || '--',
        droppingOff: droppingOff || '--',
      };
    },
    [deliveries],
  );

  const trackAnalytics = useCallback(
    () => {
      dispatch(
        TrackGADuck.creators.trackGAExpressHomepageWebClick('VIEW_ONGOING', {
          ONGOING_BOOKINGS: deliveries.length,
        }),
      );
      dispatch(ScribeDuck.creators.scribeExpressHomePageWebOngoingOrder());
    },
    [deliveries],
  );

  const onDefaultClick = useCallback(
    () => {
      trackAnalytics();
      goToBookingsTab(GE_WEB_ORDER_STATE.ActiveDelivery);
    },
    [trackAnalytics, goToBookingsTab],
  );
  const onAllocatingClick = useCallback(
    () => {
      trackAnalytics();
      goToBookingsTab(GE_WEB_ORDER_STATE.FindingDriver);
    },
    [trackAnalytics, goToBookingsTab],
  );
  const onPickingUpClick = useCallback(
    () => {
      trackAnalytics();
      goToBookingsTab(GE_ORDER_STATE.PickingUp);
    },
    [trackAnalytics, goToBookingsTab],
  );
  const onDroppingOffClick = useCallback(
    () => {
      trackAnalytics();
      goToBookingsTab(GE_ORDER_STATE.DroppingOff);
    },
    [trackAnalytics, goToBookingsTab],
  );

  return (
    <div className={styles.ongoingOrderSection}>
      <div className={commonStyles.sectionHeader}>
        <h5 className={commonStyles.title}>
          {I18n.t('v2.homepage.orderOverview.ongoingBooking.title', {
            amt: summary.total,
          })}
        </h5>
        <DivButton className={commonStyles.cta} onClick={onDefaultClick}>
          {I18n.t('v2.homepage.orderOverview.viewAll')}
        </DivButton>
      </div>
      <div className={styles.ongoingOrderContent}>
        <Row type="flex" className={styles.orderCounts}>
          <Col md={6} xs={12}>
            <DivButton
              className={styles.sectionContainer}
              onClick={onDefaultClick}
            >
              <h4>{summary.processing}</h4>
              <h6>{I18n.t('v2.orderState.geWebState.SCHEDULED')}</h6>
            </DivButton>
          </Col>
          <Col md={6} xs={12}>
            <DivButton
              className={styles.sectionContainer}
              onClick={onAllocatingClick}
            >
              <h4>{summary.allocating}</h4>
              <h6>{I18n.t('v2.orderState.geWebState.ALLOCATING')}</h6>
            </DivButton>
          </Col>
          <Col md={6} xs={12}>
            <DivButton
              className={styles.sectionContainer}
              onClick={onPickingUpClick}
            >
              <h4>{summary.pickingUp}</h4>
              <h6>{I18n.t('v2.orderState.geState.PICKING_UP')}</h6>
            </DivButton>
          </Col>
          <Col md={6} xs={12}>
            <DivButton
              className={styles.sectionContainer}
              onClick={onDroppingOffClick}
            >
              <h4>{summary.droppingOff}</h4>
              <h6>{I18n.t('v2.orderState.geState.DROPPING_OFF')}</h6>
            </DivButton>
          </Col>
        </Row>
        <ExceptMobile>
          <Steps className={styles.step}>
            <Steps.Step
              className={styles.step1}
              icon={<Icon type="reload" />}
            />
            <Steps.Step
              className={styles.step2}
              icon={<Icon type="clock-circle" />}
            />
            <Steps.Step
              className={styles.step3}
              icon={<img src={require('public/images/upload.svg')} alt="" />}
            />
            <Steps.Step
              className={styles.step4}
              icon={<img src={require('public/images/download.svg')} alt="" />}
            />
            <Steps.Step />
          </Steps>
        </ExceptMobile>
      </div>
    </div>
  );
};

OngoingOrderSection.propTypes = {
  goToBookingsTab: PropTypes.func.isRequired,
};

export default OngoingOrderSection;
